// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { of } from 'rxjs/internal/observable/of';
import { catchError } from 'rxjs/operators';

// --- 3rd Party
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { retryBackoff } from 'backoff-rxjs';

// --- HS Code
import { environment } from '../../../../environments/environment';
import { SortDirections, ApplicationError, SurveySummaryModel, ErrorMessage } from '../../../models';
import { AppService } from '../app.service';
import { RespondentSurveyBaseService } from './respondent-survey-base.service';
import { ResponseBaseService } from '../survey/response-base.service';
import { Observable } from 'rxjs';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class RespondentSurveyService extends RespondentSurveyBaseService {

  constructor(
    private http: HttpClient,
    private appService: AppService,
    private translate: TranslateService,
    responseService: ResponseBaseService
  ) {
    super(responseService);
  }

  loadSummary(
    pageSize: number,
    pageNumber: number,
    filter: string,
    sortItem: string,
    sortDirection: SortDirections
  ): void {
    let url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.respondentSurvey +
      '?' +
      `pageSize=${pageSize}&pageNumber=${pageNumber}&sortItem=${sortItem}&sortDirection=${sortDirection}`;

    if (filter) {
      url += `&filter=${filter}`;
    }

    this.appService.setLoading(true);

    this.http
      .get<SurveySummaryModel[]>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._summariesError.bind(this)),
      )
      .subscribe((result: SurveySummaryModel[]) => {
        const mappedResult = result.map((r) => {
          r.isLocked = moment(r.lockDate).isBefore(moment());
          return r;
        });
        this._surveyList$.next(mappedResult);
        this.appService.setLoading(false);
      });
  }

  loadSurveyById(id: string): void {

    const currentSelectedSurvey = this.getSurveyById(id);

    if (currentSelectedSurvey) {
      this._selectedSurvey$.next(currentSelectedSurvey);
      return;
    }

    const url =
      environment.apiConfig.surveyApiUrl +
      environment.apiConfig.respondentSurvey +
      `/${id}`;
    this.appService.setLoading(true);

    this.http
      .get<SurveySummaryModel>(url)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._summaryError.bind(this))
      )
      .subscribe(result => {
        this.appService.setLoading(false);
        this._selectedSurvey$.next(result);
      });
  }

  private _summariesError(err: any): Observable<SurveySummaryModel[]> {
    this._handleError(err);
    return of([]);
  }

  private _summaryError(err: any): Observable<SurveySummaryModel | null> {
    this._handleError(err);
    return of(null);
  }

  private _handleError(err: any) {
    this.appService.setLoading(false);

    const errorMessage: string = this.translate.instant('errorMessageGenericBackend');

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'RespondentSurveyService',
      false,
      err
    );

    this.appService.setLastError(error);

    this._surveyList$.next([]);
  }
}
